import { useMutation } from "@tanstack/react-query";
import {
  CreateQuoteAddressDto,
  CreateQuoteLineRequestDto,
  CreateQuoteRequestDto,
  CreateQuoteResultDto,
  QuotesClient,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";

export interface CreateQuoteVariables {
  name: string;
  reference?: string;
  orgUnitId?: number;
  description?: string;
  items: {
    productId: number;
    quantity: number;
    sourceCartLineNumber?: number;
    sourceQuoteItemDescriptor?: string;
  }[];
  shipTo?: {
    street: string;
    city: string;
    stateProvince: string;
    postalCode: string;
    country: string;
  };
  shippingMethodId?: number;
  includeTax?: boolean;
  emailOnCreate?: boolean;
  emailRecipients?: string[];
  emailMessage?: string;
  sendCopyToSelf?: boolean;
  currencyId?: number;
}

// Constructs the generated DTOs (which carry the toJSON the NSwag client needs) so callers pass plain shapes.
export default function useCreateQuote() {
  const api = useApi(QuotesClient);

  return useMutation<CreateQuoteResultDto, Error, CreateQuoteVariables>({
    mutationFn: (vars) =>
      api.createQuote(
        new CreateQuoteRequestDto({
          name: vars.name,
          reference: vars.reference,
          orgUnitId: vars.orgUnitId,
          description: vars.description,
          currencyId: vars.currencyId,
          shipTo: vars.shipTo
            ? new CreateQuoteAddressDto({
                street: vars.shipTo.street,
                city: vars.shipTo.city,
                stateProvince: vars.shipTo.stateProvince,
                postalCode: vars.shipTo.postalCode,
                country: vars.shipTo.country,
              })
            : undefined,
          items: vars.items.map(
            (i) =>
              new CreateQuoteLineRequestDto({
                productId: i.productId,
                quantity: i.quantity,
                sourceCartLineNumber: i.sourceCartLineNumber,
                sourceQuoteItemDescriptor: i.sourceQuoteItemDescriptor,
              }),
          ),
          shippingMethodId: vars.shippingMethodId,
          includeTax: vars.includeTax,
          emailOnCreate: vars.emailOnCreate ?? false,
          emailRecipients: vars.emailRecipients ?? [],
          emailMessage: vars.emailMessage,
          sendCopyToSelf: vars.sendCopyToSelf ?? false,
        }),
      ),
  });
}
